import React from 'react';
import { Languages } from 'lucide-react';
import { useJazyk } from '../../context/LanguageContext';
import Vlajka from './Vlajka';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

/**
 * Přepínač jazyka.
 *
 * Dřív to byla dvě tlačítka, CS a EN. Jazyků je víc a řada zkratek přes půl
 * lišty se na mobilu nevejde — proto rozbalovací seznam.
 *
 * Dokud byly položky jen text, stačil obyčejný `<select>`. Vlaječka je
 * obrázek a do `<option>` obrázek nepatří, takže je pod tím Radix: ovládání
 * klávesnicí, čtečky obrazovky i zavírání kliknutím vedle obstarává sám.
 *
 * Ve sbaleném stavu je vidět vlaječka a zkratka, v rozbaleném i celý název
 * jazyka napsaný tím jazykem — „ES" pozná ten, kdo ho hledá, „Español" i ten,
 * kdo ne. Vlaječka je navíc; sama o sobě by nestačila, protože jazyk a země
 * nejsou totéž.
 *
 * Překládá se rozhraní, ne obsah: příspěvky, zkušenosti a popisy, které
 * napsali lidé, zůstávají v jazyce, ve kterém vznikly.
 */
export default function LanguageSwitch() {
  const { jazyk, jazyky, prepni, t } = useJazyk();
  const zvoleny = jazyky.find((j) => j.kod === jazyk);

  return (
    <Select value={jazyk} onValueChange={prepni}>
      <SelectTrigger
        aria-label={t('obecne.jazyk')}
        data-testid="prepinac-jazyka"
        // Na úzkém displeji jde pryč ikona i šipka a zbyde vlaječka se
        // zkratkou. Lišta na 375 px je plná a přepínač je to jediné, co ji
        // přetéká; zkratka zůstává, protože vlajka sama jazyk neurčuje.
        className="h-auto w-auto gap-1.5 rounded-lg border-border/50 bg-transparent px-2 py-1.5
                   text-xs font-semibold text-bloom-sub hover:text-bloom-violet
                   focus:ring-1 focus:ring-bloom-violet/40
                   [&>svg:last-child]:hidden sm:[&>svg:last-child]:block"
      >
        <Languages className="hidden sm:block w-3.5 h-3.5 text-bloom-sub/60 shrink-0"
                   aria-hidden="true" />
        <SelectValue>
          <span className="flex items-center gap-1.5">
            <Vlajka kod={jazyk} />
            {zvoleny?.zkratka}
          </span>
        </SelectValue>
      </SelectTrigger>

      <SelectContent className="min-w-[11rem]">
        {jazyky.map((j) => (
          <SelectItem key={j.kod} value={j.kod} data-testid={`jazyk-${j.kod}`} className="text-xs">
            <span className="flex items-center gap-2">
              <Vlajka kod={j.kod} />
              <span className="font-semibold">{j.zkratka}</span>
              <span className="text-bloom-sub">{j.nazev}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
